import React, {useState} from "react"
import './Styles.css'
import './Root.css'
import {Footer} from "./Footer";
import {useSendProviderInquiry} from "../util/RpcHooks/SendProviderInquiryEmail";
import {ISendProviderInquiryRequest, SendProviderInquiryRequest} from "../provider_api";
import {ActionButton} from "../util/UI/ActionButton";
import {findError} from "../components/input/InputTypes";
import {Link} from "react-router-dom";
import {getBookingHost} from "../util/HostUtils";


/**
 * This is at the root path. It shows a marketing page
 * for why Kensu is great for practitioners. This does not require authentication.
 */
export const Root = () => {

  // Which image is shown / feature is highlighted, like "Seamless Scheduling & Booking", etc. 
  const [featureHighlight, setFeatureHighlight] = useState(0);

  return <div className="RootPage">
    <div className={'AppSection BeigeSection'}>
      <div className={'AppSectionContent DiscoveryCallSection'}>
        <h1>Reimagining wellness through connection and care</h1>
        <p>At Kensu, care lives at the intersection of intuition and science—a balance of East, West, and
          everything between. Join our community of practitioners changing how the world experiences care.</p>
        <Link to={'/discover'}>
          <ActionButton>START APPLICATION</ActionButton>
        </Link>
        <p className={'Subtext'}>It'll only take 3-5 minutes.</p>
      </div>
    </div>

    <div className={'AppSection GreenSection'}>
      <div className={'AppSectionContent WhatIsKensuSection'}>
        <video
            className={'HomePageVideo'}
            preload={'metadata'} autoPlay loop muted playsInline disablePictureInPicture>
          <source src={require('../images/landing_page_anim.mp4')} type={'video/mp4'}/>
        </video>

        <div className={'WhatIsKensuCaption'}>
          <h1>What is Kensu?</h1>
          <p><b>Kensu (健寿)</b> - means health and longevity.</p>
          <p>It’s our guiding principle—helping holistic practitioners run their businesses smoothly, connect with
            clients
            seeking meaningful care, and grow within a community of peers who share their dedication to integrative
            wellness.</p>
        </div>
      </div>
    </div>

    <div className={'AppSection WhiteSection'}>
      <div className={'AppSectionContent FocusSection'}>
        <h1>Focus on the healing, we’ll handle the rest</h1>

        <div className={'FocusSectionRow'}>
          <div className={'FocusSectionLeft'}>
            <img src={require('../../src/images/survey_welcome_slice@0.25x.jpg')}/>
          </div>
          <div className={'FocusSectionRight'}>

            <div className={featureHighlight == 0 ? 'FocusSectionHighlight' : 'FocusSectionNoHighlight'}
                 onMouseOver={() => setFeatureHighlight(0)}>
              <h4>Seamless Scheduling & Booking</h4>
              <p>Let clients book easily with automated reminders that keep your days balanced and full</p>
            </div>

            <div className={featureHighlight == 1 ? 'FocusSectionHighlight' : 'FocusSectionNoHighlight'}
                 onMouseOver={() => setFeatureHighlight(1)}>
              <h4>Simplified Payments & Billing</h4>
              <p>Accept payments and manage memberships and packages effortlessly—so your business flows as smoothly as
                your sessions.</p>
            </div>

            <div className={featureHighlight == 2 ? 'FocusSectionHighlight' : 'FocusSectionNoHighlight'}
                 onMouseOver={() => setFeatureHighlight(2)}>
              <h4>Relationships Rooted in Care</h4>
              <p>Connect with clients who value holistic wellness, and keep their details organized for truly
                personalized care.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className={'AppSection MediumGreenSection'}>
      <div className={'AppSectionContent HowItWorksSection'}>
        <h1>How it Works</h1>
        <div className={'HowItWorksRow'}>
          <div className={'HowItWorksThird'}>
            <div className={'HowItWorksNumberHeader'}>
              <div className={'HowItWorksEmptySpacer'}/>
              <div className={'HowItWorksNumber'}>1</div>
              <div className={'HowItWorksSpacer'}/>
            </div>
            <div className={'HowItWorksContent'}>
              <h3>Create a Profile</h3>
              <span>Add your services, team, and location(s)</span>
            </div>
          </div>

          <div className={'HowItWorksThird'}>
            <div className={'HowItWorksNumberHeader'}>
              <div className={'HowItWorksSpacer'}/>
              <div className={'HowItWorksNumber'}>2</div>
              <div className={'HowItWorksSpacer'}/>
            </div>
            <div className={'HowItWorksContent'}>
              <h3>Connect with our Team</h3>
              <span>We’ll learn more about your practice to ensure Kensu is the right fit</span>
            </div>
          </div>

          <div className={'HowItWorksThird'}>
            <div className={'HowItWorksNumberHeader'}>
              <div className={'HowItWorksSpacer'}/>
              <div className={'HowItWorksNumber'}>3</div>
              <div className={'HowItWorksEmptySpacer'}/>
            </div>
            <div className={'HowItWorksContent'}>
              <h3>Go Live</h3>
              <div>Start getting discovered by Kensu members near you</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className={'AppSection BeigeSection'}>
      <div className={'AppSectionContent BeforeYouJoinSection'}>
        <h1>Before you join</h1>
        <h3>A few answers to help you learn more about the Kensu community</h3>

        {faqs.map(faq => <BeforeYouJoinDropdown title={faq.title} text={faq.text}/>)}

        <div className={'BeforeYouJoinMoreFaqs'}>
          Read more Frequently Asked Questions <a target={'_blank'}
                                                  href={`${getBookingHost()}/library/article/jyvPIZoOhHx7XjhvpLOma/`}>here</a>.
        </div>
      </div>
    </div>


    <div className={'AppSection WhiteSection'}>
      <div className={'AppSectionContent YourWorkMattersSection'}>
        <h1>Your work matters more than ever</h1>
        <p>
          <b>75%</b> of Americans say they want to prioritize wellness—
          yet <b>60%</b> can’t find the services or guidance they need. We created Kensu to bridge that gap, connecting
          those searching for healing with practitioners who can truly support them.
        </p>
        <p>
          Our mission is simple: to connect people with trusted practitioners and make holistic wellness part of
          everyday care.
        </p>

        <div>
          <img className={'FoundersImage'} src={require('../../src/images/home_founders_sq.jpeg')}/>
        </div>

        <div>
          <img className={'SignatureImage DarkModeInvert'} src={require('../../src/images/carlie_christine.png')}/>
        </div>

      </div>
    </div>

    <Footer/>

  </div>

}

const BeforeYouJoinDropdown = (props: { title: string, text: string }) => {

  const [open, setOpen] = useState(false);

  return <div className={'BeforeYouJoinDropdown'}>

    <div className={'BeforeYouJoinHeader'} onClick={() => setOpen(!open)}>
      <img alt={'open/close'}
           src={open ? require('../../src/images/icons/arrow_down.png') : require('../../src/images/icons/arrow_right.png')}/>
      <h3>{props.title}</h3>
    </div>

    {open && <div className={'BeforeYouJoinText'}>{props.text}</div>}

  </div>
}

const faqs = [
  {
    title: 'Who can join Kensu?',
    text: 'Kensu is open to licensed or credentialed holistic health practitioners—including acupuncturists, herbalists, nutritionists, bodyworkers, and integrative care providers. Every applicant is reviewed to ensure alignment with our values, standards of care, and commitment to holistic well-being'
  },
  {
    title: 'How does the vetting process work?',
    text: 'Every practitioner undergoes a thorough review process that includes application screening, credential verification, and referrals from trusted members of the holistic community. This ensures quality, professionalism, and trust across the Kensu network.'
  },
  {
    title: 'What does Kensu offer practitioners?',
    text: 'Kensu provides all-in-one tools for booking, billing, and client management, along with visibility to clients seeking holistic care. You’ll also gain access to a curated community of practitioners for learning, collaboration, and referrals—helping you grow your practice and impact.'
  },
  {
    title: 'Is there a cost to join?',
    text: 'Yes. Pricing starts as low as $20/month, with flexible membership options tailored to your practice size and goals. Details are discussed during your discovery call once we’ve learned more about your needs.'
  },
]